import {
  addDays,
  dmy,
  dutyAt,
  dutyKindsOf,
  dutyStartsAt,
  MarkIndex,
  shortName,
  todayIso,
  type MarksResponse,
} from '@kancelaria/shared';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, errorText } from '../../api/client.js';
import { useData } from '../../store/data.js';
import { toast } from '../../store/ui.js';
import { DateInput } from '../DatePicker.js';
import { Field, Modal } from '../ui.js';

/** Де людина в наряді цієї доби: заступає, стоїть з минулих діб чи здає. */
type DutyState = 'starts' | 'stays' | 'ends';

const STATE_NOTE: Record<DutyState, string> = { starts: '', stays: 'стоїть', ends: 'здає' };

/**
 * Хто в наряді обраної доби (за замовчуванням сьогодні): і ті, хто заступає,
 * і ті, хто стоїть з минулих діб чи здає. Довгий наряд (бойовий розподіл)
 * почався кілька днів тому, і без них список виходив неповним.
 */
export function DutyTodayModal({ onClose }: { onClose: () => void }): ReactNode {
  const settings = useData(s => s.session!.org.settings);
  const people = useData(s => s.people);
  const units = useData(s => s.units);
  const [date, setDate] = useState(todayIso());
  const [index, setIndex] = useState<MarkIndex | null>(null);
  // Дата, за яку зараз показано список: між перемиканнями старий лишається, щоб вікно не стрибало.
  const [shown, setShown] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .get<MarksResponse>(`/api/marks?from=${addDays(date, -1)}&to=${date}`)
      .then(r => {
        if (!alive) return;
        setIndex(MarkIndex.from(r.marks));
        setShown(date);
      })
      .catch(e => toast(errorText(e), 'err'));
    return () => {
      alive = false;
    };
  }, [date]);

  /** Наряди за видами: у кожного — люди, що стоять у ньому цієї доби; спершу ті, хто заступає. */
  const groups = useMemo(() => {
    if (!index || !shown) return [];
    const kinds = dutyKindsOf(settings);
    const mealCount = settings.meals.length;
    const meals = Array.from({ length: mealCount }, (_, mi) => mi);
    const order: Record<DutyState, number> = { starts: 0, stays: 1, ends: 2 };
    return kinds
      .map(code => {
        const type = settings.markTypes.find(t => t.code === code);
        const who = people
          .filter(p => p.active)
          .flatMap(p => {
            const marks = index.person(p.id);
            if (!meals.some(mi => dutyAt(marks, shown, mi) === code)) return [];
            const state: DutyState = meals.some(mi => dutyStartsAt(marks, shown, mi, code, mealCount))
              ? 'starts'
              : dutyAt(marks, shown, mealCount - 1) === code
                ? 'stays'
                : 'ends';
            return [{ person: p, state }];
          })
          .sort((a, b) => order[a.state] - order[b.state]);
        return { code, label: type?.label ?? code, who };
      })
      .filter(g => g.who.length);
  }, [index, settings, people, shown]);

  return (
    <Modal title="Хто в наряді" onClose={onClose} width={520}>
      <div className="field-row">
        <Field label="Дата">
          <DateInput value={date} onChange={setDate} />
        </Field>
        <div className="btn-row" style={{ marginBottom: 12 }}>
          {[
            { label: 'Сьогодні', days: 0 },
            { label: 'Завтра', days: 1 },
          ].map(p => {
            const value = addDays(todayIso(), p.days);
            return (
              <button
                key={p.label}
                className={`btn${date === value ? ' primary' : ''}`}
                onClick={() => setDate(value)}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
      {!index || !shown ? (
        <div className="note">Завантажую…</div>
      ) : groups.length ? (
        groups.map(g => (
          <section key={g.code} style={{ marginBottom: 12 }}>
            <h4 style={{ margin: '0 0 6px' }}>
              {g.label} <span className="pill">{g.who.length}</span>
            </h4>
            {g.who.map(({ person: p, state }) => (
              <div key={p.id}>
                {shortName(p)}
                {p.unitId ? <span className="muted">, {units.find(u => u.id === p.unitId)?.name ?? ''}</span> : null}
                {STATE_NOTE[state] ? <span className="muted"> — {STATE_NOTE[state]}</span> : null}
              </div>
            ))}
          </section>
        ))
      ) : (
        <div className="note">На {dmy(shown ?? date)} ніхто не в наряді.</div>
      )}
    </Modal>
  );
}
